import React, {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Button,
  Divider,
  Typography,
  IconButton,
} from "@material-ui/core";
import {
  CallSplit as SplitIcon,
  Check as CheckIcon,
  Close as CancelIcon,
  PersonOutline as PersonIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  ErrorOutline as AlertIcon,
  WarningRounded as WarningIcon,
} from "@material-ui/icons";
import { useTranslation } from "../../hooks/useTranslation";
import SpeakerBadge from "./SpeakerBadge";
import TimestampInput from "./TimestampInput";
import ConfirmDialog, {
  useConfirmPreference,
} from "../ConfirmDialog";
import { computeSplitStartTime } from "../../utils/transcriptionFormat";

/* Chave usada para persistir a preferência "não mostrar mais a confirmação
   de marcar frase como revisada". Definida fora do componente para que
   tenha referência estável e seja fácil pesquisar/alterar futuramente. */
const CONFIRM_MARK_REVIEWED_PREF = "transcription:markReviewed";

/* Limites de confiança definidos pelo produto:
   - confidence >= HIGH_CONFIDENCE          → OK (sem destaque visual)
   - LOW_CONFIDENCE <= confidence < HIGH    → "atenção" (ícone amarelo)
   - confidence < LOW_CONFIDENCE            → "alerta" (ícone vermelho)
   A cor não é mais aplicada ao TEXTO da frase para não prejudicar a
   leitura: o sinal visual fica em um ícone à esquerda da frase. */
const HIGH_CONFIDENCE = 0.9;
const LOW_CONFIDENCE = 0.85;

/**
 * Quebra um texto em uma sequência alternada de [substring, isMatch].
 * Usado para renderizar o destaque dos termos da pesquisa sem usar
 * `dangerouslySetInnerHTML`.
 */
function buildHighlightTokens(text, query) {
  const safeText = text || "";
  const safeQuery = (query || "").trim();
  if (!safeQuery) return [{ text: safeText, match: false }];

  /* Escapa caracteres regex para segurança ao usar no `RegExp`. */
  const escaped = safeQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(escaped, "gi");
  const tokens = [];
  let lastIndex = 0;
  let match;
  /* eslint-disable-next-line no-cond-assign */
  while ((match = regex.exec(safeText)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ text: safeText.slice(lastIndex, match.index), match: false });
    }
    tokens.push({
      text: safeText.slice(match.index, match.index + match[0].length),
      match: true,
    });
    lastIndex = match.index + match[0].length;
    if (match[0].length === 0) {
      /* evita loop infinito em padrões vazios */
      regex.lastIndex += 1;
    }
  }
  if (lastIndex < safeText.length) {
    tokens.push({ text: safeText.slice(lastIndex), match: false });
  }
  return tokens;
}

/**
 * Classifica o nível de confiança em uma das três faixas usadas pela UI.
 *
 * @returns {"low"|"medium"|"ok"} `ok` quando não há motivo de destaque.
 */
function classifyConfidence(confidence) {
  if (confidence == null || !Number.isFinite(confidence)) return "ok";
  if (confidence < LOW_CONFIDENCE) return "low";
  if (confidence < HIGH_CONFIDENCE) return "medium";
  return "ok";
}

/**
 * Renderiza uma sentença da transcrição (área 4, item da lista).
 *
 * O componente é controlado: recebe o estado e dispara callbacks. Toda a
 * lógica de mutação fica no `TranscriptionList` / `TranscriptionReviewPanel`,
 * mantendo este componente focado em apresentação + edição local do texto.
 *
 * IMPORTANTE: o componente é envolto por `React.memo` para evitar a cascata
 * de re-renders quando o parágrafo ativo muda no player (a cada nova frase).
 * Por isso, todos os callbacks recebem `index` como primeiro argumento e
 * devem ter referência estável no `TranscriptionList` — não use lambdas
 * inline lá. Veja `TranscriptionList.js`.
 *
 * Interação:
 *   - CLIQUE NO TEXTO        → faz `onSeek(start)` (leva o player para o
 *                              instante em que a frase começa).
 *   - LÁPIS (botão de edição) → entra no modo de edição do texto.
 *   - LIXEIRA                → pede confirmação e dispara `onDelete`.
 *
 * Props:
 *   - `sentence`        objeto da sentença
 *   - `index`           posição na lista (passada de volta nos callbacks)
 *   - `speakers`        lista completa para resolver cor/nome
 *   - `prevSentence`    objeto da sentença anterior (ou null) — usado para
 *                       limites de tempo
 *   - `nextSentence`    objeto da sentença posterior (ou null)
 *   - `editable`        boolean
 *   - `active`          boolean — frase corrente do player (recebe destaque)
 *   - `searchQuery`     string da busca atual
 *   - `searchActiveMatchOccurrence` número opcional — índice (0-based) da
 *                       OCORRÊNCIA do termo dentro DESTA sentença que está
 *                       focada pela navegação prev/next (-1 = nenhum). Não
 *                       é o offset no texto: é o "qual deles" (1º, 2º, ...).
 *   - `onUpdate(index, updates)`        atualizar campos da sentença
 *   - `onChangeSpeaker(index, spId)`    trocar speaker_id da sentença
 *   - `onSplit(index, {updates, newSentence})` cortar a sentença a partir do
 *                       cursor (ver lógica abaixo)
 *   - `onDelete(index)` remover a sentença (após confirmação do usuário)
 *   - `onSeek(seconds)` pedido para mover o player para esse instante
 */
const TranscriptionParagraph = forwardRef(function TranscriptionParagraph(
  {
    sentence,
    index,
    speakers,
    prevSentence,
    nextSentence,
    editable,
    active,
    searchQuery,
    searchActiveMatchOccurrence,
    onUpdate,
    onChangeSpeaker,
    onSplit,
    onDelete,
    onSeek,
  },
  ref,
) {
  const { t } = useTranslation("common");
  const [editing, setEditing] = useState(false);
  const [textDirty, setTextDirty] = useState(false);
  const [speakerMenuAnchor, setSpeakerMenuAnchor] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  /* Diálogo de "marcar como revisada" quando o usuário clica no ícone de
     alerta de confiança. Confirmar dispara `onUpdate` com confidence=1.0,
     o que faz o ícone sumir (`classifyConfidence` retorna "ok"). */
  const [confirmReviewedOpen, setConfirmReviewedOpen] = useState(false);
  const editableRef = useRef(null);

  /* Quando entra em edição, populamos o conteúdo do contenteditable
     diretamente via `textContent` em um efeito disparado por `editing`.
     Não usamos `dangerouslySetInnerHTML` porque ele re-aplica em cada
     render e poderia descartar o que o usuário acabou de digitar. */
  useEffect(() => {
    if (editing && editableRef.current) {
      editableRef.current.textContent = sentence?.text || "";
      setTextDirty(false);
      requestAnimationFrame(() => {
        const el = editableRef.current;
        if (!el) return;
        el.focus();
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      });
    }
    /* Quando `sentence.text` muda externamente (ex.: split em outra
       sentença, ou re-fetch), só precisamos sincronizar quando NÃO
       estamos editando, para não descartar o texto digitado. */
    if (!editing && editableRef.current) {
      editableRef.current.textContent = sentence?.text || "";
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [editing, sentence?.text]);

  const speaker = useMemo(
    () => speakers?.find((s) => s.speaker_id === sentence?.speaker_id),
    [speakers, sentence?.speaker_id],
  );

  const beginEdit = (e) => {
    if (e) e.stopPropagation();
    if (!editable) return;
    setTextDirty(false);
    setEditing(true);
  };

  const finishEdit = (commit) => {
    if (!editing) return;
    if (commit) {
      const newText = (editableRef.current?.textContent || "").trim();
      const oldText = (sentence?.text || "").trim();
      if (newText && newText !== oldText) {
        /* Edição manual confirmada → confiança vai para 1.0 (ver decisão
           do produto, idem ao split). */
        onUpdate?.(index, { text: newText, confidence: 1.0 });
      }
    } else if (editableRef.current) {
      editableRef.current.textContent = sentence?.text || "";
    }
    setTextDirty(false);
    setEditing(false);
  };

  const handleSplit = () => {
    if (!editable) return;
    const el = editableRef.current;
    const fullText = el?.textContent || sentence?.text || "";

    /* Determina onde cortar: usamos a posição da seleção, recuando até o
       início da palavra mais próxima — assim, mesmo que o cursor esteja no
       meio de uma palavra, a divisão respeita limites de palavra. */
    let cutAt = fullText.length;
    const sel = window.getSelection?.();
    if (sel && sel.rangeCount > 0 && el && el.contains(sel.anchorNode)) {
      const range = sel.getRangeAt(0).cloneRange();
      range.setStart(el, 0);
      cutAt = range.toString().length;
    }
    while (cutAt > 0 && /\S/.test(fullText.charAt(cutAt - 1))) {
      cutAt -= 1;
    }
    while (cutAt < fullText.length && !/\S/.test(fullText.charAt(cutAt))) {
      cutAt += 1;
    }
    if (cutAt <= 0 || cutAt >= fullText.length) {
      /* Sem ponto válido (cursor na borda); não há o que dividir. */
      return;
    }

    const originalText = fullText.slice(0, cutAt).trimEnd();
    const newText = fullText.slice(cutAt).trimStart();
    if (!originalText || !newText) return;

    const newStart = computeSplitStartTime(sentence, nextSentence);
    /* Conforme decisão do produto: ambas as frases ganham confiança 1.0,
       já que houve edição manual. Também ajustamos o `end` da frase original
       para `newStart` — caso contrário ele continuaria cobrindo o áudio
       INTEIRO da fala (que agora foi dividida), criando overlap artificial
       com a nova frase e quebrando a validação dos limites de tempo. */
    const originalEndCandidate = Number.isFinite(sentence?.end)
      ? Math.min(sentence.end, newStart)
      : newStart;
    const originalEnd = Math.max(
      originalEndCandidate,
      Number.isFinite(sentence?.start) ? sentence.start : 0,
    );
    const updates = { text: originalText, confidence: 1.0, end: originalEnd };
    const nextStart =
      nextSentence && Number.isFinite(nextSentence.start)
        ? nextSentence.start
        : null;
    const newEnd =
      nextStart != null && nextStart > newStart
        ? Math.min(nextStart, newStart + 5)
        : newStart + 5;
    const newSentence = {
      text: newText,
      start: newStart,
      end: newEnd,
      speaker_id: sentence?.speaker_id,
      confidence: 1.0,
    };
    onSplit?.(index, { updates, newSentence });
    setTextDirty(false);
    setEditing(false);
  };

  const handleSeekToSentence = (e) => {
    if (e) e.stopPropagation();
    if (editing) return;
    if (typeof onSeek === "function" && Number.isFinite(sentence?.start)) {
      /* Passamos `end` como segundo argumento para que o painel possa
         agendar a pausa automática no fim da frase clicada (requisito de
         "tocar uma frase de cada vez"). Quando `end` não estiver
         definido, caímos para `nextSentence.start` ou nada. */
      const pauseAt = Number.isFinite(sentence?.end)
        ? sentence.end
        : Number.isFinite(nextSentence?.start)
          ? nextSentence.start
          : undefined;
      onSeek(sentence.start, pauseAt);
    }
  };

  const handleConfirmDelete = () => {
    setConfirmDeleteOpen(false);
    onDelete?.(index);
  };

  /* Limites para edição do tempo da frase atual.
     A validação serve para manter a ORDENAÇÃO cronológica da lista (que é
     ordenada por `start`). Para isso basta exigir
     `prev.start < current.start < next.start`. Usar `prev.end` aqui
     produziria limites colados ao próprio `start` atual (frases adjacentes
     do Deepgram costumam ter `prev.end ≈ current.start`) e impediria
     qualquer edição "para mais cedo" que ainda mantivesse a ordem.
     Sobreposições parciais entre frases adjacentes são permitidas — elas
     acontecem naturalmente quando dois speakers se interrompem. */
  const minSeconds = Number.isFinite(prevSentence?.start)
    ? prevSentence.start
    : null;
  const maxSeconds = Number.isFinite(nextSentence?.start)
    ? nextSentence.start
    : null;

  const tokens = useMemo(
    () => buildHighlightTokens(sentence?.text || "", searchQuery),
    [sentence?.text, searchQuery],
  );

  const confidencePct = Math.round((sentence?.confidence || 0) * 100);
  const confidenceLevel = classifyConfidence(sentence?.confidence);
  const confidenceTooltip =
    sentence?.confidence != null
      ? t("transcription.list.confidenceTooltip", { value: confidencePct })
      : "";
  const confidenceAlertTooltip =
    confidenceLevel === "low"
      ? t("transcription.list.confidenceAlertLow", { value: confidencePct })
      : confidenceLevel === "medium"
        ? t("transcription.list.confidenceAlertMedium", { value: confidencePct })
        : "";
  /* Os ícones de alerta/atenção só fazem sentido enquanto há revisão em
     curso — em modo somente-leitura (efectiveMode "READ_ONLY" ou
     "APPROVED_LOCKED" no painel pai → `editable=false`) o usuário não
     pode marcar como revisada de qualquer jeito, e a ideia é apresentar
     a transcrição como "fechada"/oficial. Por isso ocultamos o ícone
     completamente nesses casos. */
  const showConfidenceAlert = editable && confidenceLevel !== "ok";

  /* Verifica preferência "não mostrar mais" (por usuário) para o popup
     de confirmação de "marcar como revisada". Se o usuário já optou por
     pular, aplicamos a alteração diretamente — caso contrário, abrimos
     o dialog. */
  const { shouldSkip: shouldSkipMarkReviewed } = useConfirmPreference(
    CONFIRM_MARK_REVIEWED_PREF,
  );

  const applyMarkReviewed = () => {
    /* Marcar como revisada = confidence 1.0 → ícone some no próximo render. */
    onUpdate?.(index, { confidence: 1.0 });
  };

  const requestMarkReviewed = () => {
    if (shouldSkipMarkReviewed()) {
      applyMarkReviewed();
      return;
    }
    setConfirmReviewedOpen(true);
  };

  const handleConfirmReviewed = () => {
    setConfirmReviewedOpen(false);
    applyMarkReviewed();
  };

  const rootClassName = [
    "transcription-paragraph",
    active ? "is-active" : "",
    editing ? "is-editing" : "",
    showConfidenceAlert ? `has-confidence-${confidenceLevel}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  /* Wrapper de tooltip do texto: aplica a mensagem de confiança (ou, na
     falta dela, o tooltip de seek) sempre que NÃO estamos em edição.
     Quando o parágrafo é editável, mostramos a dica combinada (clique
     para reproduzir + duplo-clique para editar) para que o usuário
     descubra o atalho de duplo-clique sem precisar do botão de lápis. */
  const seekTooltipKey =
    editable && !editing
      ? "transcription.list.textSeekAndEditTooltip"
      : "transcription.list.textSeekTooltip";
  const textTooltipTitle = !editing
    ? confidenceTooltip ||
      (Number.isFinite(sentence?.start) ? t(seekTooltipKey) : "")
    : "";

  /* Componente do ícone de alerta de confiança (renderizado na "coluna de
     ações" do parágrafo, logo à esquerda do botão de edição). Devolvemos
     um elemento ou `null`, conforme o estado de confiança. */
  const renderConfidenceAlert = () => {
    if (!showConfidenceAlert) return null;
    return (
      <Tooltip
        title={confidenceAlertTooltip || confidenceTooltip}
        placement="top"
      >
        <span>
          <IconButton
            size="small"
            className={[
              "transcription-paragraph__alert-btn",
              `is-${confidenceLevel}`,
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={(e) => {
              e.stopPropagation();
              if (editable) requestMarkReviewed();
            }}
            disabled={!editable}
            aria-label={confidenceAlertTooltip || confidenceTooltip}
          >
            {confidenceLevel === "low" ? (
              <AlertIcon fontSize="small" />
            ) : (
              <WarningIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  return (
    <Box ref={ref} className={rootClassName} data-active={active ? "true" : "false"}>
      <Box className="transcription-paragraph__row">
        <TimestampInput
          seconds={sentence?.start || 0}
          editable={editable && !editing}
          minSeconds={minSeconds}
          maxSeconds={maxSeconds}
          onChange={(newStart) => onUpdate?.(index, { start: newStart })}
          /* Em modo somente leitura, clicar no tempo equivale a clicar
             na frase: tocar a partir de `start - 1s` e parar antes da
             próxima. Por isso passamos o mesmo wrapper com `pauseAt`. */
          onSeek={(s) => {
            if (typeof onSeek !== "function") return;
            const pauseAt = Number.isFinite(sentence?.end)
              ? sentence.end
              : Number.isFinite(nextSentence?.start)
                ? nextSentence.start
                : undefined;
            onSeek(s, pauseAt);
          }}
          className="transcription-paragraph__time"
        />
        <Box className="transcription-paragraph__speaker">
          <SpeakerBadge
            speaker={speaker}
            speakers={speakers}
            variant="inline"
            onClick={
              editable
                ? (e) => setSpeakerMenuAnchor(e.currentTarget)
                : undefined
            }
            endAdornment={
              editable ? (
                <ArrowDropDownIcon
                  fontSize="small"
                  className="transcription-paragraph__speaker-arrow"
                />
              ) : null
            }
          />
        </Box>

        <Box className="transcription-paragraph__text-col">
          {editing ? (
            <Box className="transcription-paragraph__text-wrapper">
              <div
                ref={editableRef}
                className="transcription-paragraph__editable"
                contentEditable
                suppressContentEditableWarning
                spellCheck
                role="textbox"
                aria-multiline="true"
                onInput={() => {
                  /* Marca a edição como suja para feedback visual e para
                     habilitar o botão de confirmação imediatamente. */
                  if (!textDirty) setTextDirty(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    finishEdit(true);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    finishEdit(false);
                  }
                }}
              />
            </Box>
          ) : (
            <Tooltip
              title={textTooltipTitle}
              placement="top"
              arrow
              disableHoverListener={!textTooltipTitle}
            >
              <Box
                className={[
                  "transcription-paragraph__text-wrapper",
                  Number.isFinite(sentence?.start) ? "is-clickable" : "",
                  editable ? "is-editable-on-dblclick" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={handleSeekToSentence}
                /* Duplo-clique entra em modo de edição (atalho ao botão de
                   lápis). Mantemos o `onClick` de seek: é idempotente, e
                   alguns usuários esperam o duplo-clique para selecionar
                   palavra — entrar em edição também atende a essa intenção. */
                onDoubleClick={editable ? beginEdit : undefined}
                role={Number.isFinite(sentence?.start) ? "button" : undefined}
                tabIndex={Number.isFinite(sentence?.start) ? 0 : undefined}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSeekToSentence(e);
                  }
                }}
              >
                <Typography
                  component="div"
                  className="transcription-paragraph__text"
                >
                  {(() => {
                    /* Iteramos os tokens (alternância texto/match) e
                       mantemos um contador de quantos matches já vimos
                       até aqui. Quando o índice da ocorrência coincide
                       com `searchActiveMatchOccurrence`, aplicamos a
                       classe `is-current` para destacar essa ocorrência
                       específica (a "1 de N" da navegação). */
                    let matchSeen = -1;
                    return tokens.map((tok, i) => {
                      if (!tok.match) {
                        return <span key={`tk-${i}`}>{tok.text}</span>;
                      }
                      matchSeen += 1;
                      const isCurrent =
                        searchActiveMatchOccurrence != null &&
                        searchActiveMatchOccurrence === matchSeen;
                      return (
                        <span
                          key={`tk-${i}`}
                          className={
                            isCurrent
                              ? "transcription-paragraph__match is-current"
                              : "transcription-paragraph__match"
                          }
                        >
                          {tok.text}
                        </span>
                      );
                    });
                  })()}
                </Typography>
              </Box>
            </Tooltip>
          )}

          {editing && (
            <Box className="transcription-paragraph__edit-actions">
              <Button
                size="small"
                variant="outlined"
                startIcon={<CancelIcon fontSize="small" />}
                onClick={() => finishEdit(false)}
                className="transcription-paragraph__edit-btn"
              >
                {t("cancel")}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SplitIcon fontSize="small" />}
                onClick={handleSplit}
                className="transcription-paragraph__edit-btn"
              >
                {t("transcription.list.splitSentence")}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={!textDirty}
                startIcon={<CheckIcon fontSize="small" />}
                onClick={() => finishEdit(true)}
                className="transcription-paragraph__edit-btn"
              >
                {t("transcription.list.acceptEdit")}
              </Button>
            </Box>
          )}
        </Box>

        {/* Coluna de ações (alerta + lápis + lixeira). Mantemos os três
            agrupados em uma única célula da grid para que fiquem visualmente
            alinhados como uma "barra de ações" da frase. O ícone de alerta
            aparece apenas quando há alerta de confiança; quando não há,
            preservamos um espaçamento mínimo para alinhar os parágrafos
            entre si. */}
        <Box className="transcription-paragraph__actions-col">
          {renderConfidenceAlert()}
          {editable && !editing && (
            <Tooltip title={t("transcription.list.editText")} placement="top">
              <IconButton
                size="small"
                onClick={beginEdit}
                className="transcription-paragraph__edit-btn-icon"
                aria-label={t("transcription.list.editText")}
              >
                <EditIcon style={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          )}
          {editable && !editing && (
            <Tooltip title={t("transcription.list.deleteSentence")} placement="top">
              <IconButton
                size="small"
                onClick={() => setConfirmDeleteOpen(true)}
                className="transcription-paragraph__delete-btn"
                aria-label={t("transcription.list.deleteSentence")}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Menu
        anchorEl={speakerMenuAnchor}
        open={Boolean(speakerMenuAnchor)}
        onClose={() => setSpeakerMenuAnchor(null)}
      >
        <MenuItem disabled>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("transcription.list.changeSpeakerHeader")}
          />
        </MenuItem>
        <Divider />
        {(speakers || []).map((sp) => (
          <MenuItem
            key={sp.speaker_id}
            selected={sp.speaker_id === sentence?.speaker_id}
            onClick={() => {
              setSpeakerMenuAnchor(null);
              if (sp.speaker_id !== sentence?.speaker_id) {
                onChangeSpeaker?.(index, sp.speaker_id);
              }
            }}
          >
            <SpeakerBadge speaker={sp} speakers={speakers} variant="inline" />
          </MenuItem>
        ))}
      </Menu>

      <ConfirmDialog
        open={confirmReviewedOpen}
        title={t("transcription.list.confidenceMarkReviewedTitle")}
        message={t("transcription.list.confidenceMarkReviewedText")}
        confirmLabel={t("transcription.list.confidenceMarkReviewedConfirm")}
        confirmIcon={<CheckIcon fontSize="small" />}
        onClose={() => setConfirmReviewedOpen(false)}
        onConfirm={handleConfirmReviewed}
        preferenceKey={CONFIRM_MARK_REVIEWED_PREF}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        title={t("transcription.list.confirmDeleteTitle")}
        message={t("transcription.list.confirmDeleteText")}
        confirmLabel={t("transcription.list.confirmDeleteConfirm")}
        confirmColor="secondary"
        confirmIcon={<DeleteIcon fontSize="small" />}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
});

/* `React.memo` é crítico aqui: o painel chama `onTimeUpdate` a 10Hz durante
   reprodução, mudando o `activeSentenceIndex` da lista. Sem a memoização,
   todos os parágrafos (potencialmente centenas) re-renderizariam a cada
   100ms, congelando a aba do navegador. Com memo, apenas os 2 parágrafos
   afetados (o que perde `active` e o novo) re-renderizam. Para isso valer,
   o `TranscriptionList` precisa garantir referências estáveis nos
   callbacks/objetos passados como props. */
export default React.memo(TranscriptionParagraph);
